'use client';

import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Input, CustomButton, Modal, LoadingButton } from '@/components/atomic';
import { ICONS, IMAGES } from '@/constants/constants';
import Image from 'next/image';
import SocialLogins from '@/components/signin/SocialLogins';
import ForgotPasswordModal from '@/components/auth/ForgotPasswordModal';
import ResetLinkSentModal from '@/components/auth/ResetLinkSentModal';
import CreateNewPasswordModal from '@/components/auth/CreateNewPasswordModal';
import PasswordChangedModal from '@/components/auth/PasswordChangedModal';
import GetSMSModal from '@/components/auth/GetSMSModal';
import SMSLinkSentModal from '@/components/auth/SMSLinkSentModal';
import Link from 'next/link';
import { useDispatch } from 'react-redux';
import { useRouter, useSearchParams } from 'next/navigation';
import { signInUser } from '@/lib/api/user';
import { forgotPassword } from '@/lib/api/user';
import { setAuthTokens } from '@/lib/slices/authSlice';
import { useToastContext } from '@/components/ui/ToastProvider';
import { useValidation } from '@/hooks/useValidation';
import { handleApiError } from '@/lib/handelApiError';
import { decodeJWT } from '@/lib/utils/jwt';

const Login = () => {

  const { toast } = useToastContext();
  const searchParams = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalState, setModalState] = useState<'forgot' | 'sent' | 'sms' | 'smsSent' | 'newPassword' | 'changed'>('forgot');
  const [resetEmail, setResetEmail] = useState('');
  const [resetPhoneNumber, setResetPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const errorShownRef = useRef<string | null>(null);

  const dispatch = useDispatch();
  const router = useRouter();

  const { validate } = useValidation();

  // Handle URL error parameters
  useEffect(() => {
    const errorParam = searchParams.get('error');
    
    if (errorParam && errorParam !== errorShownRef.current) {
      // Decode the URL-encoded error message
      const decodedError = decodeURIComponent(errorParam);
      
      // Show the error toast
      toast.error(decodedError);
      
      // Mark this error as shown to prevent duplicates
      errorShownRef.current = errorParam;
      
      // Clean up the URL by removing the error parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({ ...prev, [name]: '' })); // Clear error on change
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate(formData, { requireNames: ['email', 'password'], useSimplePassword: true });
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsLoading(true);
    try {
      const response = await signInUser(formData);
      if (response.success) {
        const { tokens } = response.data;
        // Tokens are already set in cookies by signInUser function with JWT expiration
        dispatch(setAuthTokens(tokens));
        toast.success(response.message);
        router.push('/dashboard');
      } else {
        toast.error(response.message || 'Sign in failed');
      }
    } catch (error: unknown) {
      handleApiError(error, toast);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSuccess = (email: string) => {
    setResetEmail(email);
    setModalState('sent');
  };

  const handleTryDifferentEmail = () => {
    setModalState('forgot');
  };

  const handleSwitchToSMS = () => {
    setModalState('sms');
  };

  const handleSMSSuccess = (phoneNumber: string) => {
    setResetPhoneNumber(phoneNumber);
    setModalState('smsSent');
  };

  const handleResendRequest = async () => {
    try {
      const response = await forgotPassword(resetEmail);
      toast.success(response.message || 'Reset link sent!');
    } catch (error: unknown) {
      handleApiError(error, toast);
    }
  };

  const handleNewPasswordSuccess = () => {
    setModalState('changed');
  };

  const handleLoginNow = () => {
    handleCloseModal();
    // Here you would typically redirect to the main app or refresh the login form

  };

  const handleBackToEmail = () => {
    setModalState('forgot');
  };

  const handleTryDifferentPhone = () => {
    setModalState('sms');
  };

  const handleSMSResendRequest = async () => {
    try {
      // Import and use forgotPasswordBySMS here
      const { forgotPasswordBySMS } = await import('@/lib/api/user');
      const response = await forgotPasswordBySMS(resetPhoneNumber);
      toast.success(response.message || 'SMS sent successfully!');
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      if (Array.isArray(apiMessage)) {
        apiMessage.forEach((msg: string) => toast.error(msg));
      } else if (typeof apiMessage === 'string') {
        toast.error(apiMessage);
      } else {
        toast.error('Failed to send SMS.');
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalState('forgot');
    setResetEmail('');
    setResetPhoneNumber('');
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row-reverse overflow-hidden bg-white h-full font-sans">

      {/* Background Image Section - Now shows on mobile above the form */}
      <div className="w-full lg:w-1/2 relative overflow-hidden h-[60vh] lg:h-auto">
        {/* Background Image */}
        <Image
          src={IMAGES.signin}
          alt="Ferry on water"
          fill
          className="object-cover"
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0" />

        {/* Content Overlay */}
        <div className="absolute inset-0 flex flex-col justify-end items-center text-white p-8 pb-16">

          {/* Main Content - Now at the Bottom */}
          <div className="text-center max-w-lg text-gray-200 flex flex-col items-center justify-center ">
            <h2 className="font-rakesly text-3xl font-bold mb-4 tracking-wide max-md:text-xl">
              CREATE AN ACCOUNT
            </h2>

            <p className="font-sans  mb-6 leading-relaxed text-md max-md:text-xs">
              Create an account to make it easier to fill your information and store your bookings
            </p>

            <Link href={'/create-account'}>
              <CustomButton className="cursor-pointer border-2 border-white text-white px-20 py-3 rounded-lg font-sans font-medium hover:bg-white hover:text-black transition-all duration-300 min-w-[140px] max-md:px-15 max-md:py-2">
                Register
              </CustomButton></Link>

          </div>
        </div>
      </div>

      <div className="w-full h-full justify-between lg:w-1/2  bg-white flex flex-col p-4 lg:p-8">


        <div>
          <div className='flex flex-col'>
            <h1 className='font-rakesly text-4xl font-bold text-custom-gray tracking-wide'>SIGN IN</h1>
            <Image src={ICONS.redline} alt='redline' width={300} height={80} />
          </div>

          <div className='flex flex-col gap-4 mt-10'>
            <Input name="email" label='Email' type='text' className='w-full py-3' value={formData.email} onChange={handleInputChange} error={errors.email} placeholder="Enter your email" />
            <Input name="password" label='Password' type='simple-password' className='w-full py-3' value={formData.password} onChange={handleInputChange} error={errors.password} placeholder="••••••••" />
          </div>
        </div>

        {/* footer */}
        <div className='flex flex-col gap-4 mt-5'>

          <div className="text-left pt-2 font-sans">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Forgotten your password?
            </button>
          </div>
          <LoadingButton loading={isLoading} onClick={handleSubmit} disabled={isLoading} className='cursor-pointer bg-gray-800 border-2 border-white text-white px-20 py-3 rounded-lg font-sans font-medium  transition-all duration-300 min-w-[140px]'>
            {isLoading ? 'Signing In...' : 'Sign In'}
          </LoadingButton>
          <div className='flex flex-col gap-4 mt-10'>
            <SocialLogins />
          </div>
        </div>


      </div>

      {/* Forgot Password Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        {modalState === 'forgot' && (
          <ForgotPasswordModal
            onBack={handleCloseModal}
            onSuccess={handleForgotPasswordSuccess}
            onSwitchToSMS={handleSwitchToSMS}
          />
        )}
        {modalState === 'sent' && (
          <ResetLinkSentModal
            email={resetEmail}
            onTryDifferentEmail={handleTryDifferentEmail}
            onResendRequest={handleResendRequest}
          />
        )}
        {modalState === 'sms' && (
          <GetSMSModal
            onBack={handleBackToEmail}
            onSuccess={handleSMSSuccess}
          />
        )}
        {modalState === 'smsSent' && (
          <SMSLinkSentModal
            phoneNumber={resetPhoneNumber}
            onTryDifferentPhone={handleTryDifferentPhone}
            onResendRequest={handleSMSResendRequest}
          />
        )}
        {modalState === 'newPassword' && (
          <CreateNewPasswordModal
            onSuccess={handleNewPasswordSuccess}
          />
        )}
        {modalState === 'changed' && (
          <PasswordChangedModal
            onLoginNow={handleLoginNow}
          />
        )}
      </Modal>
    </div>
  );
};

export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Login />
    </Suspense>
  );
}